import _ from 'lodash'
import moment from 'moment'
import $ from '@src/plugins/vue-gridform/shared/helper'
import { FIELDS } from '@src/plugins/vue-gridform/shared/fieldtypes'
import Randomizer from '@src/plugins/vue-gridform/shared/randomizer'
import appConfig from '@src/app.config'
import CONSTANTS from '../constants'

const _BASE_URL = process.env.VUE_APP_API_BASE_URL || ''
const _ID_LENGTH = 36
const _LOGO = require('@assets/images/192x192.png')
const _COACHING_VALIDATION_PROPS = ['id', 'name', 'agentId', 'coachingData']

const Helper = {
  // BASE_URL/api/path/id?query
  apiUrl(path, id, query) {
    const params = _.isArray(id) ? `/${id.join('/')}` : id ? `/${id}` : ''
    const q = query ? `?${query}` : ''
    return `${_BASE_URL}/api/${path}${params}${q}`
  },
  isValidId(id) {
    return typeof id === 'string' && id.length === _ID_LENGTH
  },

  // convert db template to gridform config
  templateToConfig(template) {
    if (!template) return

    const templateData = this.parseJSON(template.templateData)
    if (templateData && Array.isArray(templateData.config)) return templateData.config

    // each block is has a gridform config
    let blocks = []
    _.get(template, 'buildingBlocks', []).forEach(block => {
      blocks = blocks.concat(this.blockToConfig(block))
    })

    return blocks
  },
  templateToFields(template) {
    let blocks = []

    const templateData = this.parseJSON(template.templateData)
    if (templateData && Array.isArray(templateData.config)) {
      blocks = blocks.concat(this.configToFields(templateData.config))
    }

    const buildingBlocks = _.get(template, 'buildingBlocks', [])
    buildingBlocks.forEach(block => {
      const cblocks = this.blockToFields(block)
      blocks = blocks.concat(cblocks)
    })

    return blocks
  },
  templateToGroups(template) {
    if (!template) return []

    let groups = []
    const templateData = this.parseJSON(template.templateData)
    if (templateData && Array.isArray(templateData.config)) {
      const tgroups = _.groupBy(_.flatten(templateData.config), 'fieldlist.id')
      _.each(tgroups, frows => {
        // NOTE all fields without fieldlist will be grouped by "_NOGROUP"
        // $.initFieldlist()
        const fl = _.first(frows).fieldlist || _.clone(CONSTANTS.DEFAULT_FIELDLIST)
        const rows = [] // unflat rows
        frows.forEach(row => {rows.push([row])})
        const group = {...fl, rows}
        // no initials, guess it
        if (!group.initials) {
          const header = _.flatten(rows).find(col => col.type === 'subheader')
          if (header && header.label) group.initials = String(header.label).toLocaleUpperCase()
          group.initials = group.initials || group.id
        }
        groups.push(group)
      })
    }

    return groups
  },
  // prepend individual config params
  // prepend header (user/coach, employee)
  // append footer (date, reminder, scriptures)
  // append notes
  // NOTE this function must be called with the vue instance for translation
  templateToCoaching(template, params) {
    console.assert(!!this.$vuetify, 'wrong context, need vue instance')
    if (!this.$vuetify) return
    if (!_.isFunction(_.get(this.$vuetify, 'lang.t'))) return

    const i18n = key => this.$vuetify.lang.t(key)
    const hintUsername = i18n('$vuetify.entity.user')
    const hintEmployee = i18n('$vuetify.entity.employee')
    const headFL = {initials: '_HEADER'}
    const head = [
      [
        {name: 'username', hint: hintUsername, dense: true, forceDisabled: true, fieldlist: headFL},
        {name: 'employee', hint: hintEmployee, dense: true, forceDisabled: true, fieldlist: headFL}
      ]
    ]
    if (!_.isEmpty(params)) {
      let hintLink
      let valueLink
      if (params.id) {
        hintLink = i18n('$vuetify.entity.voixenid')
        head.push([
          {name: 'voixenid', value: params.id, hint: hintLink, dense: true, forceEnabled: false, clearable: false, fieldlist: headFL}
        ])
      }
      if (params.link) {
        hintLink = i18n('$vuetify.entity.link')
        // ex. https://interactionanalytics.com/search/find?q=_id:id
        valueLink = decodeURIComponent(params.link)
        head.push([
          {name: 'link', type: 'link', value: valueLink, hint: hintLink, dense: true, forceEnabled: true, clearable: false, fieldlist: headFL}
        ])
      }
      if (params.user) {
        hintLink = i18n('$vuetify.entity.voixenuser')
        head.push([
          {name: 'voixenuser', value: params.user, hint: hintLink, dense: true, forceEnabled: false, clearable: false, fieldlist: headFL}
        ])
      }
    }

    const hintDate = i18n('$vuetify.entity.date')
    const hintTime = i18n('$vuetify.entity.time')
    const hintReminder = i18n('$vuetify.entity.reminder')
    const hintScriptureUser = i18n('$vuetify.entity.scriptureUser')
    const hintScriptureEmployee = i18n('$vuetify.entity.scriptureEmployee')
    const footFL = {initials: '_FOOTER'}
    const foot = [
      [
        {name: 'coachingDate', value: '', hint: hintDate, type: 'date', dense: true, fieldlist: footFL},
        {name: 'coachingDateTime', value: '', hint: hintTime, type: 'time', dense: true, fieldlist: footFL},
        {name: 'reminder', value: '', hint: hintReminder, type: 'date', dense: true, fieldlist: footFL},
        {name: 'reminderTime', value: '', hint: hintTime, type: 'time', dense: true, fieldlist: footFL}
      ],
      [
        {name: 'scriptureUser', hint: hintScriptureUser, dense: true, forceDisabled: true, fieldlist: footFL},
        {name: 'scriptureEmployee', hint: hintScriptureEmployee, dense: true, forceDisabled: true, fieldlist: footFL}
      ]
    ]

    // const hintNotes = i18n('$vuetify.notes.name')
    // const hintNotesPrivate = i18n('$vuetify.notes.private')
    const notesInitials = i18n('$vuetify.notes.initials')
    const notesDescription = i18n('$vuetify.notes.description')
    // NOTE when we set id the fieldlist is rendered as block
    // TODO set internal depending on coachingNotesPrivate?
    const notesFL = {
      id: '_NOTES',
      // name: hintNotes,
      initials: notesInitials,
      description: notesDescription,
      noRating: true,
      internal: true,
      collapsed: true
    }
    const notes = [
      [
        {name: 'coachingNotes', hint: notesDescription, type: 'textarea', rows: 5, dense: true, rating: false, fieldlist: notesFL}
      ]
    ]

    const tpls = []
    tpls.push({config: head, name: 'Kopf', initials: 'Kopf'})

    const config = Helper.templateToConfig(template)
    _.flatten(config).forEach(col => {
      // set unique field names
      col.name = _.uniqueId(`cb-${_.now()}-`)
      // set fieldlist to collapsed
      if (col.fieldlist) col.fieldlist.collapsed = true
      // NOTE reset current score
      if (col.rating) col.rating.score = 0
    })
    tpls.push({config, name: template.description, initials: template.name})

    tpls.push({config: notes, name: 'Notes', initials: 'Notes'})
    tpls.push({config: foot, name: 'Fuß', initials: 'Fuß'})

    let rows = []
    tpls.forEach(tpl => {rows = rows.concat(tpl.config)})

    return rows
  },
  blockToConfig(block) {
    const blockData = this.parseJSON(_.get(block, 'buildingBlockData'))
    return Array.isArray(blockData) ? blockData : blockData.config
  },
  blockToFields(block) {
    const blocks = []
    const blockData = this.parseJSON(_.get(block, 'buildingBlockData'))

    if (blockData) {
      const config = Array.isArray(blockData) ? blockData : blockData.config
      return this.configToFields(config)
    }

    return blocks
  },
  configToFields(config) {
    if (_.isEmpty(config)) return [] // return empty array for concat

    const fields = []
    config.forEach(row => {
      row.forEach(field => {
        const icon = FIELDS[field.type || 'text'].icon
        fields.push({...{icon}, ...field})
      })
    })
    return fields
  },
  coachingToConfig(coaching) {
    if (!coaching) return

    const coachingData = this.parseJSON(coaching.coachingData)
    return coachingData && coachingData.config ? coachingData.config : []
  },
  coachingToData(coaching) {
    if (!coaching) return

    return this.parseJSON(coaching.coachingData)
  },
  coachingToRows(coaching) {
    const config = this.coachingToConfig(coaching)
    // only columns with fieldlist
    return _.flatten(config).filter(col => _.get(col, 'fieldlist.id'))
  },
  coachingToRating(coaching) {
    const config = this.coachingToConfig(coaching)
    return $.ratingsGroupedSum(config)
  },
  coachingToGroups(coaching, options) {
    options = options || {}
    const config = this.coachingToConfig(coaching)
    const cols = _.flatten(config).filter(col => {
      if (options.internal === false && _.get(col, 'fieldlist.internal')) return false
      col.fieldlist = col.fieldlist || _.clone(CONSTANTS.DEFAULT_FIELDLIST)
      return _.get(col, 'fieldlist.id')
    })
    return _.groupBy(cols, 'fieldlist.id')
  },
  coachingToGroupsRating(coaching) {
    return $.ratingsGrouped(this.coachingToConfig(coaching))
  },
  groupCoachingsByFinished(coachings, format = 'YYYY-MM-DD') {
    const groups = {}
    let finished
    _.each(coachings, coaching => {
      finished = coaching.finished
      // NOTE should not happen, temporary because of missing audit
      if (coaching.isFinished && !finished) finished = coaching.created
      if (finished) {
        const id = moment.utc(finished).local().format(format)
        groups[id] = groups[id] || []
        groups[id].push({...coaching})
      }
    })
    return groups
  },
  coachingDates(gfData) {
    const fieldCoachingDate = _.get(_.find(gfData, data => data.name === 'coachingDate'), 'value')
    const fieldCoachingTime = _.get(_.find(gfData, data => data.name === 'coachingDateTime'), 'value', '12:00')
    const fieldReminderDate = _.get(_.find(gfData, data => data.name === 'reminder'), 'value')
    const fieldReminderTime = _.get(_.find(gfData, data => data.name === 'reminderTime'), 'value', '08:00')
    const coachingDate = fieldCoachingDate ? moment(`${fieldCoachingDate} ${fieldCoachingTime}`).toISOString() : ''
    const reminderDate = fieldReminderDate ? moment(`${fieldReminderDate} ${fieldReminderTime}`).toISOString() : ''
    return {coachingDate, reminderDate}
  },
  coachingValidate(coaching) {
    return !_.isEmpty(coaching)
      && _.isObject(coaching)
      && _.every(_COACHING_VALIDATION_PROPS, prop => _.has(coaching, prop))
  },

  buildLabel({ initials, name, description }) {
    let label = ''
    if (initials) label += `[${initials}]`
    if (name) label += ` ${name}`
    if (description) label += ` : ${description}`
    return label
  },
  buildTemplateTreeItems(template, params = {}) {
    const rex = params.rex
    const groups = this.templateToGroups(template)
    const items = []

    if (_.isEmpty(groups) && !_.isEmpty(template.fields)) {
      console.log('no groups??')
      _.each(template.fields, field => {
        items.push({
          id: field.name,
          label: `${field.label} (${field.hint || ''})`,
          icon: _.get(FIELDS, `${field.type || 'text'}.icon`)
        })
      })
      return items
    }

    _.each(groups, group => {
      const children = []
      _.each(_.flatten(group.rows), field => {
        const ftype = _.get(FIELDS, `${field.type || 'text'}`)
        let label = `${field.label || ftype.label || ''}`
        if (field.hint) label += ` (${field.hint || ftype.hint || ''})`
        if (!rex || rex.test(label)) {
          const details = []
          if (params.details) {
            // if (field.hint) details.push({id: 'hint', label: field.hint})
            details.push({id: 'rating', label: `min: ${field.rating.min}, max: ${field.rating.max}, score: ${field.rating.score}`})
          }

          children.push({
            id: field.name,
            label,
            icon: ftype.icon,
            children: details
          })
        }
      })

      // insert group if children found or rex matches
      const addGroup = rex
        ? rex.test(group.initials) || rex.test(group.name) || rex.test(group.description)
        : false
      if (!_.isEmpty(children) || addGroup) {
        items.push({
          id: group.id,
          label: this.buildLabel(group),
          icon: 'mdi-file-document-edit',
          children
        })
      }
    })

    return items
  },

  sanitizeConfig(config) {
    const _config = _.cloneDeep(config)
    _.flatten(_config).forEach(c => {
      if (c.fieldlist && _.isUndefined(c.fieldlist.weighting)) c.fieldlist.weighting = 100
      c.type = c.type || 'text'
      // in template2.json the grade field is predefined with this min and max values
      // editing a template with a grade field otherwise opens "discard changes dialog"
      if (c.type === 'radio' && /^note/.test(c.value)) {
        c.min = 0
        c.max = 6
      }
    })
    return _config
  },
  findExportFields(coaching) {
    const config = this.coachingToConfig(coaching)
    return _.flatten(config)//.filter(col => col.export)
  },

  resetConfigValues(coachingData) {
    const parsed = this.parseJSON(coachingData)
    parsed.duration = 0
    parsed.username = ''
    parsed.coachingDate = ''
    _.each(_.flatten(parsed.config), col => {
      if (/^cb-/.test(col.name)) {
        col.value = null
        // TODO set min or 0?
        if (col.rating) col.rating.score = 0
        // if (col.rating) col.rating.score = col.rating.min
      }
      if (/^(coachingDate|coachingDateTime|reminder|reminderTime)$/.test(col.name)) {
        col.value = ''
      }
    })
    coachingData = JSON.stringify(parsed)
    return coachingData
  },
  randomizeConfigValues(coachingData) {
    const parsed = _.isString(coachingData) ? this.parseJSON(coachingData) : coachingData

    parsed.duration = _.random(100, 600)

    _.each(_.flatten(parsed.config), col => {
      // exclude header and footer fields
      if (!/^(_HEADER|_FOOTER)$/.test(_.get(col, 'fieldlist.initials'))) {
        // set unique field names
        col.name = _.uniqueId(`cb-${_.now()}-`)
        // set fieldlist to collapsed
        if (col.fieldlist) col.fieldlist.collapsed = true
        if (!col.forceDisabled) col.value = Randomizer.sample(col)
        if (col.rating) col.rating = $.rating(col)
      }
    })

    return JSON.stringify(parsed)
  },

  // groupings
  groupCoachingsByEmployeeAndTemplate(coachings) {
    const children = []
    const coachingsWithRating = coachings.filter(coaching => !!coaching.rating)
    const grouped = _.groupBy(coachingsWithRating, 'agentId')
    _.each(grouped, coachings => {
      const employeeChildren = []
      const groupedTemplate = _.groupBy(coachings, 'templateInitials')
      _.each(groupedTemplate, (coachings, templateName) => {
        const templateChildren = []
        _.each(coachings, coaching => {
          if (coaching.rating.percent) {
            templateChildren.push({
              value: coaching.rating.percent,
              coachingId: coaching.id
            })
          }
        })
        if (templateChildren.length) {
          employeeChildren.push({
            name: templateName,
            children: templateChildren
          })
        }
      })
      if (employeeChildren.length) {
        const name = _.first(coachings).employeeName
        children.push({
          name,
          children: employeeChildren
        })
      }
    })
    return {name: 'groupEmployeeAndTemplate', children}
  },
  groupCoachingsByTemplateAndEmployee(coachings) {
    const children = []
    const coachingsWithRating = coachings.filter(coaching => !!coaching.rating)
    const grouped = _.groupBy(coachingsWithRating, 'templateInitials')
    _.each(grouped, (coachings, templateName) => {
      const templateChildren = []
      const groupedTemplate = _.groupBy(coachings, 'agentId')
      _.each(groupedTemplate, coachings => {
        const employeeChildren = []
        _.each(coachings, coaching => {
          if (coaching.rating.percent) {
            employeeChildren.push({
              value: coaching.rating.percent,
              coachingId: coaching.id
            })
          }
        })
        if (employeeChildren.length) {
          const name = _.first(coachings).employeeName
          templateChildren.push({
            name,
            children: employeeChildren
          })
        }
      })
      if (templateChildren.length) {
        children.push({
          name: templateName,
          children: templateChildren
        })
      }
    })
    return {name: 'groupTemplateAndEmployee', children}
  },

  translateTemplateType(type) {
    const _type = (type || 'text').toLowerCase()
    switch (_type) {
      case 'textarea':
        return 'Langtext'
      case 'combobox':
        return 'Mehrfachauswahl'
      case 'slider':
        return 'Bereich'
      case 'radio':
        return 'Auswahl'
      case 'switch':
        return 'Ja/Nein'
      case 'date':
        return 'Datum'
    }
    return _type
  },
  /**
   * Convert counts to short format. 1000 = 1k, 100.000 = 100k, 1.000.000 = 1M
   * @param {(number|string)} count - number to format
   * @return {string} formatted count
   * @static
   * @public
   */
  formatCount(count) {
    count = parseInt(count, 10)
    if (!_.isNumber(count) || _.isNaN(count)) return String(count)

    let f = String(count)
    let s

    if (count > 1000000) {
      s = (f / 1000000).toFixed(1)
      s = s.replace(/\.0$/, '')
      f = '' + s + 'M'
    } else if (count > 100000) {
      s = (f / 1000).toFixed(0)
      f = '' + s + 'k'
    } else if (count > 1000) {
      s = (f / 1000).toFixed(1)
      s = s.replace(/\.0$/, '')
      f = '' + s + 'k'
    }

    return f
  },

  errorHandler(error) {
    let statusCode
    let message = CONSTANTS.ERRORS.defaultMessage
    if (error) {
      message = error.toString()
      // console.dir(error)
      if (error instanceof Error) {
        statusCode = _.get(error, 'response.status')
        message = _.get(error, 'response.data.errorMessage')
        message = message || _.get(error, 'response.data.error')
        message = message || _.get(error, 'response.data')
        message = message || error.message
      }
    }

    if (statusCode === 403) message = CONSTANTS.ERRORS.status403Message

    // TODO optimize this
    if (statusCode === 406) {
      window.location.href = '/logout'
      return
    }

    window.$toast.open({
      title: CONSTANTS.ERRORS.title,
      text: message,
      type: 'error',
      timeout: -1
    })
  },

  parseJSON(str) {
    try {
      return JSON.parse(str)
    } catch (e) {}
  },

  download(content, fileName, mimeType) {
    mimeType = mimeType || 'application/octet-stream'

    const a = document.createElement('a')

    // const BOM = '\uFEFF'
    // content = BOM + content
    if (window.navigator && window.navigator.msSaveBlob) { // IE10
      navigator.msSaveBlob(new Blob([content], {type: mimeType}), fileName)
    }
    else if (window.URL && 'download' in a) { // html5 A[download]
      // TODO hide link that will triggered? could be denied by browser
      // a.style.display = 'none'
      a.style.visibility = 'hidden'
      const href = URL.createObjectURL(new Blob([content], {type: mimeType}))
      a.setAttribute('href', href)
      a.setAttribute('download', fileName)
      // TODO makes this a difference?
      a.setAttribute('target', '_blank')
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    }
    else {
      location.href = 'data:application/octet-stream,' + encodeURIComponent(content) // only this mime type is supported
    }
  },

  pdfInit() {
    const now = moment().format('DD.MM.YYYY HH:mm')
    const footer = {left: appConfig.url, right: now}
    const logo = _LOGO
    return {footer, logo}
  },

  dialogWidth(bp, small = false) {
    let width
    switch(bp) {
      case 'xs': width = '98%'; break
      case 'sm': width = '80%'; break
      case 'md': width = '70%'; break
      case 'lg': width = small ? '640px' : '60%'; break
      case 'xl': width = small ? '640px' : '50%'; break
      default: width = '60%'; break
    }
    return width
  },

  // localStorage
  getStore(key, def) {
    if (!key) return

    let data = window.localStorage.getItem(key)
    if (!data) data = def

    let json = data
    try {json = JSON.parse(data)} catch(e) {}
    return json
  },
  setStore(key, value) {
    if (!key) return

    let data = value
    if (typeof data === 'object') data = JSON.stringify(data)
    window.localStorage.setItem(key, data)
  },
  updateStore(key, value) {
    if (!key) return

    const current = this.getStore(key)
    this.setStore(key, _.extend(current, value))
  },

  // sessionStorage
  getSStore(key, def) {
    if (!key) return

    let data = window.sessionStorage.getItem(key)
    if (!data) data = def

    let json = data
    try {json = JSON.parse(data)} catch(e) {}
    return json
  },
  setSStore(key, value) {
    if (!key) return

    let data = value
    if (typeof data === 'object') data = JSON.stringify(data)
    window.sessionStorage.setItem(key, data)
  },
  updateSStore(key, value) {
    if (!key) return

    const current = this.getSStore(key)
    this.setSStore(key, _.extend(current, value))
  }
}

export default Helper
